export const factoryLocales = [
  { code: 'en', direction: 'ltr', englishName: 'English', intlLocale: 'en-US', nativeName: 'English' },
  { code: 'zh-Hans', direction: 'ltr', englishName: 'Chinese (Simplified)', intlLocale: 'zh-CN', nativeName: '中文（简体）' },
  { code: 'zh-Hant', direction: 'ltr', englishName: 'Chinese (Traditional)', intlLocale: 'zh-TW', nativeName: '中文（繁體）' },
  { code: 'es', direction: 'ltr', englishName: 'Spanish', intlLocale: 'es-ES', nativeName: 'Español' },
  { code: 'ar', direction: 'rtl', englishName: 'Arabic', intlLocale: 'ar', nativeName: 'العربية' },
  { code: 'hi', direction: 'ltr', englishName: 'Hindi', intlLocale: 'hi-IN', nativeName: 'हिन्दी' },
  { code: 'pt', direction: 'ltr', englishName: 'Portuguese', intlLocale: 'pt-BR', nativeName: 'Português' },
  { code: 'fr', direction: 'ltr', englishName: 'French', intlLocale: 'fr-FR', nativeName: 'Français' },
  { code: 'de', direction: 'ltr', englishName: 'German', intlLocale: 'de-DE', nativeName: 'Deutsch' },
  { code: 'ru', direction: 'ltr', englishName: 'Russian', intlLocale: 'ru-RU', nativeName: 'Русский' },
  { code: 'uk', direction: 'ltr', englishName: 'Ukrainian', intlLocale: 'uk-UA', nativeName: 'Українська' },
  { code: 'ja', direction: 'ltr', englishName: 'Japanese', intlLocale: 'ja-JP', nativeName: '日本語' },
  { code: 'ko', direction: 'ltr', englishName: 'Korean', intlLocale: 'ko-KR', nativeName: '한국어' },
  { code: 'tr', direction: 'ltr', englishName: 'Turkish', intlLocale: 'tr-TR', nativeName: 'Türkçe' },
  { code: 'it', direction: 'ltr', englishName: 'Italian', intlLocale: 'it-IT', nativeName: 'Italiano' },
  { code: 'pl', direction: 'ltr', englishName: 'Polish', intlLocale: 'pl-PL', nativeName: 'Polski' },
  { code: 'nl', direction: 'ltr', englishName: 'Dutch', intlLocale: 'nl-NL', nativeName: 'Nederlands' },
  { code: 'id', direction: 'ltr', englishName: 'Indonesian', intlLocale: 'id-ID', nativeName: 'Bahasa Indonesia' },
  { code: 'ms', direction: 'ltr', englishName: 'Malay', intlLocale: 'ms-MY', nativeName: 'Bahasa Melayu' },
  { code: 'vi', direction: 'ltr', englishName: 'Vietnamese', intlLocale: 'vi-VN', nativeName: 'Tiếng Việt' },
  { code: 'th', direction: 'ltr', englishName: 'Thai', intlLocale: 'th-TH', nativeName: 'ไทย' },
  { code: 'fa', direction: 'rtl', englishName: 'Persian', intlLocale: 'fa-IR', nativeName: 'فارسی' },
  { code: 'cs', direction: 'ltr', englishName: 'Czech', intlLocale: 'cs-CZ', nativeName: 'Čeština' },
  { code: 'he', direction: 'rtl', englishName: 'Hebrew', intlLocale: 'he-IL', nativeName: 'עברית' },
] as const;

export type FactoryLocaleCode = (typeof factoryLocales)[number]['code'];
export const factoryLocaleCodes = factoryLocales.map((locale) => locale.code) as unknown as readonly [
  FactoryLocaleCode,
  ...FactoryLocaleCode[],
];
export const rtlFactoryLocaleCodes = factoryLocales
  .filter((locale) => locale.direction === 'rtl')
  .map((locale) => locale.code) as unknown as readonly ['ar', 'fa', 'he'];
export const factoryLocaleManifestSource = 'OMT Verification Portal packages/operations/src/localization.ts SUPPORTED_LOCALES / LANGUAGE_OPTIONS / localeDirection';

export const maximumProductImageCount = 10;
export const maximumProductVideoCount = 1;
export const maximumProductVideoDurationSeconds = 45;
export const minimumProductSpinImageCount = 12;
export const maximumProductSpinImageCount = 36;

export function isFactoryLocaleCode(value: string): value is FactoryLocaleCode {
  return (factoryLocaleCodes as readonly string[]).includes(value);
}

export function requireFactoryLocaleCode(value: string): FactoryLocaleCode {
  if (!isFactoryLocaleCode(value)) {
    throw new Error(`Unsupported factory locale: ${value}`);
  }
  return value;
}

export function factoryLocaleNativeName(locale: FactoryLocaleCode): string {
  const found = factoryLocales.find((item) => item.code === locale);
  if (!found) throw new Error(`Missing factory locale manifest entry: ${locale}`);
  return found.nativeName;
}

const aliasMap: Readonly<Record<string, FactoryLocaleCode>> = {
  ar: 'ar',
  cs: 'cs',
  de: 'de',
  en: 'en',
  'en-GB': 'en',
  'en-US': 'en',
  es: 'es',
  fa: 'fa',
  fr: 'fr',
  he: 'he',
  hi: 'hi',
  id: 'id',
  it: 'it',
  iw: 'he',
  ja: 'ja',
  ko: 'ko',
  ms: 'ms',
  nl: 'nl',
  pl: 'pl',
  pt: 'pt',
  'pt-BR': 'pt',
  'pt-PT': 'pt',
  ru: 'ru',
  'ru-RU': 'ru',
  th: 'th',
  tr: 'tr',
  uk: 'uk',
  vi: 'vi',
  zh: 'zh-Hans',
  'zh-CN': 'zh-Hans',
  'zh-Hans': 'zh-Hans',
  'zh-Hant': 'zh-Hant',
  'zh-HK': 'zh-Hant',
  'zh-MO': 'zh-Hant',
  'zh-TW': 'zh-Hant',
};

export function matchFactoryLocale(value: string): FactoryLocaleCode | undefined {
  const normalized = value.trim().replaceAll('_', '-');
  if (!normalized) return undefined;
  const direct = aliasMap[normalized] ?? aliasMap[normalized.toLocaleLowerCase('en-US')];
  if (direct) return direct;
  const parts = normalized.split('-');
  const language = parts[0]?.toLocaleLowerCase('en-US') ?? '';
  if (language === 'zh') {
    return parts.some((part) => ['Hant', 'hant', 'TW', 'tw', 'HK', 'hk', 'MO', 'mo'].includes(part))
      ? 'zh-Hant'
      : 'zh-Hans';
  }
  return aliasMap[language];
}

export function factoryLocaleDirection(locale: FactoryLocaleCode): 'ltr' | 'rtl' {
  const found = factoryLocales.find((item) => item.code === locale);
  if (!found) throw new Error(`Missing factory locale manifest entry: ${locale}`);
  return found.direction;
}

export function factoryIntlLocale(locale: FactoryLocaleCode): string {
  return factoryLocales.find((item) => item.code === locale)?.intlLocale ?? 'en-US';
}

export function resolveFactoryLocaleCandidates(
  candidates: readonly (string | null | undefined)[],
  fallback: FactoryLocaleCode = 'en',
): FactoryLocaleCode {
  for (const candidate of candidates) {
    if (!candidate) continue;
    const matched = matchFactoryLocale(candidate);
    if (matched) return matched;
  }
  return fallback;
}

export function translationMatchesLocale(storedLocale: string, requested: FactoryLocaleCode): boolean {
  return matchFactoryLocale(storedLocale) === requested;
}

export function latestTranslationValue(
  translations: readonly { key: string; locale: string; value: string; version?: number | undefined }[],
  key: string,
  locale: FactoryLocaleCode,
): string | undefined {
  const candidates = translations.filter((item) => item.key === key && translationMatchesLocale(item.locale, locale));
  const latest = candidates.reduce<(typeof candidates)[number] | undefined>((current, item) => {
    const currentVersion = current?.version ?? 1;
    const candidateVersion = item.version ?? 1;
    return !current || candidateVersion >= currentVersion ? item : current;
  }, undefined);
  return latest?.value?.trim() ? latest.value : undefined;
}
